/**
 * Engine-hosted sales page for Freedom Gateway — a 12-month Pure Bookkeeping
 * licensee program (90 days of weekly coaching + a 4th integration month, then
 * ongoing). Bespoke emerald/gold page ported from the HubSpot LP
 * (thesuccessfulbookkeeper.com/programs/freedom-gateway, 2026-06-24).
 *
 * Rendered by ProgramSalesPage when slug === 'freedom-gateway' (mirrors Soda /
 * BKOS-License). The parent fetches /api/public/programs/freedom-gateway[?region]
 * and applies the program palette (emerald/gold) + fonts to <html> as --prog-* /
 * --font-* custom properties, so this component consumes them with fallbacks.
 *
 * REGION-AWARE (migration 0163). One program runs parallel regional cohorts
 * (North America, Australia, …). All region-variant content — coach, program
 * lead, weekly cadence, local call time, currency, and the two
 * tier cards (Solo / Ultimate) — comes from data.region.profile. NA defaults are
 * baked in below so the page renders correctly even before a region_profile is
 * seeded. The kickoff date/time is NOT profile data — it is bound live from
 * GET /api/programs/:slug/cohort-stats (see kickoffLive in the component).
 * Price + checkout routing live in commerce (cohort pricing_overrides);
 * region_profile is display-only and must agree with it.
 *
 * COMMERCE-GATED. When the resolved cohort has commerce (pricing_overrides set),
 * the tier CTAs run the buyer-intent → /order Spiffy flow. With no commerce yet,
 * the page is in waitlist mode: the CTAs capture an email for the next cohort.
 * This makes the page safe to ship before go-live (a program row is always live;
 * there is no publish flag) — it shows a waitlist until pricing is flipped on.
 *
 * Structure: hero → pains → three 90-day shifts → fit (yes/no) → the 90-day
 * transformation (4 region-aware steps) → everything you get (program + PBS) →
 * two-tier pricing (Solo / Ultimate) → testimonials → coaches → FAQ → final CTA.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProgramPublicResponse } from './ProgramSalesPage';
import { PolicyLinks } from './components/PolicyLinks';
import { CountrySelect } from './components/CountrySelect';
import { getAttributionPayload } from './attribution';
import { fetchWithRetry } from './lib/fetchRetry';

const API = import.meta.env.VITE_API_BASE_URL as string;
const PHOTO_BASE = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people';

// ────────────────────────────────────────────────────────────────────────────
// Region profile — shape + North America defaults
// ────────────────────────────────────────────────────────────────────────────

interface FgPerson {
  name: string;
  title: string;
  photo: string;
  bio: string;
}
interface FgTier {
  id: string; // 'solo' | 'ultimate' — must match commerce.plans[].id
  name: string;
  badge?: string;
  priceLabel: string;
  strikeLabel?: string;
  terms: string;
  saveLabel?: string;
  bullets: string[];
  pbsLine: string;
  highlight?: boolean;
}
interface FgRegionProfile {
  regionLabel: string;
  currency: string;
  coach: FgPerson; // weekly call host (NA: Teresa)
  lead: FgPerson; // program lead — kickoff breakout + month 4 (NA: Lisa)
  cadence: string; // 'Every Thursday, 3pm ET'
  timezoneLabel: string; // 'ET'
  // DEPRECATED — never read. The kickoff date/time is resolved LIVE from
  // GET /api/programs/:slug/cohort-stats (see kickoffLive below), never from a
  // literal. These keys still exist on older region_profile JSON in D1 and are
  // tolerated by the type so the spread in resolveProfile() stays clean, but a
  // value here can never reach the page. Fixed 2026-09-10 after the live page
  // showed "Kickoff: July 2, 2026" for two months past that date.
  kickoffDate?: string;
  kickoffLine?: string;
  kickoffNote: string;
  fastActionBonus: string | null;
  tiers: FgTier[];
}

// North America — the launch region. Confirmed with Michael 2026-06-24: Teresa
// hosts the weekly Thursday 3pm ET call; each intake's kickoff is a newbie
// breakout with Lisa on that same Thursday call (no separate kickoff slot).
// NO kickoff date lives here — it is bound live from cohort-stats at render.
const NA_DEFAULTS: FgRegionProfile = {
  regionLabel: 'North America',
  currency: 'USD',
  coach: {
    name: 'Teresa Slack',
    title: 'Your weekly Freedom Gateway coach',
    photo: `${PHOTO_BASE}/teresa-slack/photo-default.jpg`,
    bio: 'Built and sold a seven-figure bookkeeping firm with her sister Connie. Teresa hosts the weekly Freedom Gateway call — real questions, real answers on pricing, boundaries, and getting your practice onto solid ground.',
  },
  lead: {
    name: 'Lisa Campbell',
    title: 'CEO, The Successful Bookkeeper Global',
    photo: 'https://images.thesuccessfulbookkeeper.com/cdn-cgi/image/width=800,format=auto/host/c87e98b2-e099-4753-92f1-1631dc792478/cutout.png',
    bio: 'Built The Marcam Group from her living room into a high six-figure firm. Author of Beyond Bookkeeping. Designed this program because she lived everything it solves — and runs the newbie breakout that gets each new intake oriented.',
  },
  cadence: 'Every Thursday, 3pm ET',
  timezoneLabel: 'ET',
  kickoffNote:
    'Your intake kicks off on the Thursday call — Lisa runs a newbie breakout at the start to get you oriented and set up for success.',
  fastActionBonus: null,
  tiers: [
    {
      id: 'solo',
      name: 'Solo',
      badge: '20% Off',
      priceLabel: '$425 USD/mo',
      strikeLabel: '$531.25 USD/month',
      terms: '12-month commitment · 1 user · Includes Pure Bookkeeping System',
      saveLabel: 'You save $1,275 on the program + $65/mo on PBS forever',
      bullets: [
        'Kickoff breakout with Lisa on day one',
        'Weekly group coaching — Thursdays, 3pm ET, with Teresa Slack',
        'Complete Freedom Framework course curriculum',
        'Month 4 integration & planning with Lisa',
      ],
      pbsLine:
        'Pure Bookkeeping System — Basic Edition License · 365-day license included, then just $260 USD/mo (normally $325) forever',
    },
    {
      id: 'ultimate',
      name: 'Ultimate',
      badge: '40% Off — Best Value',
      priceLabel: '$695 USD/mo',
      strikeLabel: '$1,158 USD/month',
      terms: '12-month commitment · Up to 10 users · Ultimate PBS License',
      saveLabel: '40% off applied · You save $5,560 over the year',
      highlight: true,
      bullets: [
        'Everything in Solo, plus:',
        'Sales & Marketing System',
        'HR & Hiring Systems',
        'Practice management for up to 10 users',
      ],
      pbsLine:
        'Pure Bookkeeping System — Ultimate Edition License · Full PBS + Practice Management, then just $297 USD/mo forever',
    },
  ],
};

/** Merge a (possibly partial / null) region profile from the API over the NA
 *  defaults so the page always has a complete, sensible profile to render. */
function resolveProfile(data: ProgramPublicResponse): FgRegionProfile {
  const p = (data.region?.profile ?? null) as Partial<FgRegionProfile> | null;
  if (!p) return NA_DEFAULTS;
  return {
    ...NA_DEFAULTS,
    ...p,
    coach: { ...NA_DEFAULTS.coach, ...(p.coach ?? {}) },
    lead: { ...NA_DEFAULTS.lead, ...(p.lead ?? {}) },
    tiers: Array.isArray(p.tiers) && p.tiers.length ? (p.tiers as FgTier[]) : NA_DEFAULTS.tiers,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Static (region-agnostic) copy — ported from the HubSpot LP, tightened
// ────────────────────────────────────────────────────────────────────────────

const PAINS = [
  "You're working 50+ hours a week and still feel behind",
  'Every client, every question, every task runs through you',
  "You've thought about hiring but can't figure out how to hand anything off",
  "You know you need systems but don't have time to create them",
  "You went into bookkeeping for freedom — but you've built yourself a cage",
];

const SHIFTS = [
  {
    title: 'Install Proven Systems',
    body: "You don't build systems from scratch. Freedom Gateway gives you the Pure Bookkeeping System — pre-built, tested workflows built by 43,000+ bookkeepers worldwide. You install them. They work.",
  },
  {
    title: "Build Your Team's Capacity",
    body: "The system trains your team — you don't. With clear role breakdowns, every person knows exactly what they're responsible for. You stop being the help desk.",
  },
  {
    title: 'Reclaim Your Role as Owner',
    body: 'Move from doing the bookkeeping to leading the business. Your job becomes analytical reports, client conversations, and growth — not clearing bank feeds.',
  },
];

const FIT_YES = [
  'You run a bookkeeping business and do most of the work yourself',
  'You have clients but no consistent process for serving them',
  "You want to hire or delegate but don't know how to make the handoff",
  'You’re ready to invest in building a real business — not just a better job',
  "You're willing to commit to transforming how your business operates",
];
const FIT_NO = [
  "You're looking for a quick fix or a hack",
  "You haven't started your bookkeeping business yet — see Launchpad instead",
  "You're not willing to implement — this program requires action, not just learning",
];

const PROGRAM_INCLUDES = [
  '90-day guided implementation',
  'Kickoff breakout on day one',
  'Weekly group coaching calls',
  'Complete Freedom Framework course curriculum',
  'Month 4 integration and planning',
];
const PBS_INCLUDES = [
  'Full Pure Bookkeeping compliance system',
  'Practice management software access',
  '365-day license included with program',
  '20% lifetime discount on license renewal — just $260 USD/mo after year one (normally $325 USD/mo)',
];

const TESTIMONIALS = [
  {
    name: 'Rae Gagnon',
    tag: 'Restored work-life balance · More profitable',
    quote:
      'The program helped me restore work-life balance and definitely made me more profitable. I fell in love with my firm all over again. The value of this program far exceeds the investment.',
  },
  {
    name: 'Melissa Kleiderer',
    tag: 'MRK Avenue · Left full-time job · 3 team members',
    quote:
      "I've been running my firm as a solopreneur for five years but couldn't grow enough to justify going full-time. The amazing thing is I'm paying myself out of the business more than I made at that job — and I employ three part-time team members.",
  },
  {
    name: 'Jessica Hearsey',
    tag: 'Laid the foundation · Ready to grow',
    quote:
      'So glad I found you and invested in myself. I could not have laid the foundation I did without you. Ready to grow now.',
  },
  {
    name: 'Julie Healy',
    tag: 'Incredible insight · Far better position',
    quote:
      "I've gained incredible insight and knowledge and am in a far better place to build my business than I was a year ago. The people I've met have been incredible.",
  },
];

const PROOF_STATS = [
  { num: '90%', label: "of bookkeepers don't have systems in place" },
  { num: '76%', label: 'average revenue growth for members' },
  { num: '30+', label: 'hours saved per month on average' },
  { num: '18K+', label: 'bookkeepers using Pure Bookkeeping' },
];

const FAQS = [
  {
    q: 'What exactly is Freedom Gateway?',
    a: 'A 12-month program — 90 days of weekly coaching plus a fourth month of integration and planning — for bookkeeping business owners who want to install systems so their business can run without everything going through them. You get the Pure Bookkeeping System, a kickoff breakout, weekly group coaching, course content, and 12 months of community membership.',
  },
  {
    q: "What's included with the Pure Bookkeeping System?",
    a: "You get the Basic Edition license — the core Pure Bookkeeping compliance system and practice management software — included for your entire first year. After 12 months, the license continues at USD $260/month (a permanent 20% discount from the standard USD $325/month). You don't build systems from scratch — they're already built, tested, and refined by 43,000+ bookkeepers.",
  },
  {
    q: 'Who is this for?',
    a: "Bookkeepers with an established practice who are stuck doing everything themselves. You have clients and you're good at what you do — but you haven't been able to step out of the day-to-day. If you're at or before startup, our Launchpad workshop is a better starting point.",
  },
  {
    q: "What's the time commitment?",
    a: 'One coaching call per week, plus time to work through content and implement. Most members find 3–5 hours per week is realistic. The program is designed for working bookkeepers — not people with free calendars.',
  },
  {
    q: "What if I don't have a team yet?",
    a: "That's exactly who this is for. Freedom Gateway helps you install the systems that make delegation possible. Many members start solo and hire their first team member during or shortly after the program.",
  },
  {
    q: 'What happens after the 12 months?',
    a: "Your Pure Bookkeeping license continues at the discounted USD $260/month rate — forever. When you're ready, the next programs in the path (Clients, Pricing, Scale) take you deeper into specific growth areas.",
  },
  {
    q: 'How is this different from buying a course?',
    a: "Courses give you information. Freedom Gateway gives you implementation — live coaching, accountability, a proven system you actually install into your business, and the practice management software to run it. You're not watching videos alone. You're building with support.",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Buyer-intent modal (tier-aware) → order page
// ────────────────────────────────────────────────────────────────────────────

function BuyerIntentModal(props: {
  open: boolean;
  tierName: string;
  priceLabel: string;
  onClose: () => void;
  onSubmit: (d: { email: string; firstName: string; lastName: string }) => void;
}) {
  const { open, tierName, priceLabel, onClose, onSubmit } = props;
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !firstName) {
      setError('First name and email are required.');
      return;
    }
    onSubmit({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <button style={modalCloseBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
        <p style={modalEyebrow}>
          Freedom Gateway · {tierName} · {priceLabel}
        </p>
        <h2 style={modalH2}>Where should we send your checkout?</h2>
        <p style={modalSub}>
          Your name and email so the checkout pre-fills, and so the team knows you're on your way.
        </p>
        <form onSubmit={handle} style={modalForm}>
          <div style={modalRow}>
            <label style={modalLabel}>
              First name
              <input type="text" required autoFocus value={firstName}
                onChange={(e) => setFirstName(e.target.value)} style={modalInput} />
            </label>
            <label style={modalLabel}>
              Last name
              <input type="text" value={lastName}
                onChange={(e) => setLastName(e.target.value)} style={modalInput} />
            </label>
          </div>
          <label style={modalLabel}>
            Email
            <input type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} style={modalInput} />
          </label>
          {error && <p style={modalError}>{error}</p>}
          <button type="submit" style={modalSubmit}>Continue to checkout →</button>
          <p style={modalFineprint}>
            Continuing takes you to the secure checkout. Your card is charged through Spiffy — not
            stored here.
          </p>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function FreedomGatewayPage({
  slug,
  data,
  regionParam,
}: {
  slug: string;
  data: ProgramPublicResponse;
  regionParam?: string;
}) {
  const navigate = useNavigate();
  const profile = useMemo(() => resolveProfile(data), [data]);
  const hasCommerce = !!(data.commerce && data.commerce.plans && data.commerce.plans.length > 0);
  const regionCode = data.region?.code ?? regionParam ?? null;
  const cohortId = data.region?.cohortId ?? null;

  // Live kickoff When — resolved from the region cohort's first scheduled
  // session via GET /api/programs/:slug/cohort-stats, the SAME resolver that
  // powers the confirmation-email {{kickoffDateLong}} / {{kickoffTimeRange}}
  // tokens (reference pattern: BkosFoundingCohortPage, ProgramSalesPage).
  // NEVER a literal: this closed the 2026-09-10 gap where the live page kept
  // advertising "Kickoff: July 2, 2026" two months after that date passed.
  // Null while loading, on fetch failure, AND when the cohort's first session
  // is already past (the endpoint deliberately returns null kickoff fields
  // then) — every render site below degrades to date-free copy built from
  // standing facts (the weekly cadence), never a stale or wrong date.
  const [kickoffLive, setKickoffLive] = useState<{ date: string; time: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const qs = regionCode ? `?region=${encodeURIComponent(regionCode)}` : '';
    fetchWithRetry(`${API}/api/programs/${encodeURIComponent(slug)}/cohort-stats${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const date = typeof d.kickoffDateLongYear === 'string' && d.kickoffDateLongYear
          ? d.kickoffDateLongYear
          : (typeof d.kickoffDateLong === 'string' && d.kickoffDateLong ? d.kickoffDateLong : null);
        const time = typeof d.kickoffTimeRange === 'string' && d.kickoffTimeRange ? d.kickoffTimeRange : null;
        setKickoffLive(date ? { date, time } : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug, regionCode]);

  // Derived copy for the five kickoff render sites. The fallback names the
  // standing cadence (a fact that cannot expire) rather than any date.
  const kickoffLine = kickoffLive
    ? `Kickoff: ${kickoffLive.date}${kickoffLive.time ? ` · ${kickoffLive.time}` : ''}`
    : `Enrollment open · ${profile.cadence}`;
  const kickoffHeading = kickoffLive
    ? `Kickoff — ${kickoffLive.date}`
    : 'Kickoff — on your first weekly call';

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTier, setModalTier] = useState<FgTier>(profile.tiers[0]);

  // Waitlist (no-commerce mode) state — email-first two-step, matching the
  // platform pattern (ProgramRsvpView/WaitlistPage): step 1 saves the email
  // immediately, step 2 collects first name + last name + country, all
  // required (2026-09-13 — this page previously never asked for a name or
  // country at all, just email → "done").
  const [wlEmail, setWlEmail] = useState('');
  const [wlFirstName, setWlFirstName] = useState('');
  const [wlLastName, setWlLastName] = useState('');
  const [wlCountry, setWlCountry] = useState('');
  const [wlStep, setWlStep] = useState<'email' | 'details'>('email');
  const [wlState, setWlState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [wlError, setWlError] = useState('');
  const wlDetailsComplete = wlFirstName.trim() && wlLastName.trim() && wlCountry;

  const PASSTHROUGH_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'code',
  ];

  const openTier = (tier: FgTier) => {
    if (hasCommerce) {
      setModalTier(tier);
      setModalOpen(true);
    } else {
      document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleIntent = (payload: { email: string; firstName: string; lastName: string }) => {
    const inbound = new URLSearchParams(window.location.search);
    const out = new URLSearchParams({
      plan: modalTier.id,
      em: payload.email,
      fn: payload.firstName,
      ln: payload.lastName,
    });
    if (regionCode) out.set('region', regionCode);
    for (const k of PASSTHROUGH_KEYS) {
      const v = inbound.get(k);
      if (v) out.set(k, v);
    }

    // Capture the lead before they bounce to Spiffy (best-effort — navigation
    // fires regardless). Uses the 'freedom-gateway-program' buyer-intent record.
    const attribution: Record<string, string> = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = inbound.get(k);
      if (v) attribution[k] = v;
    }
    fetch(`${API}/api/buyer-intent/capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug: 'freedom-gateway-program',
        email: payload.email,
        first_name: payload.firstName,
        last_name: payload.lastName,
        source_page: window.location.pathname + window.location.search,
        attribution,
      }),
    }).then((res) => {
      // Fire-and-forget by design — checkout must never wait on this. But a
      // swallowed non-ok response was previously invisible even in the
      // console. The server logs an integration_event_log breadcrumb on the
      // same failure (missing/unpublished 'freedom-gateway-program' assessment);
      // this is the client-side echo.
      if (!res.ok) console.warn('[buyer-intent] capture for \'freedom-gateway-program\' failed', res.status);
    }).catch(() => {
      /* swallow — navigation still fires */
    });

    navigate(`/programs/${slug}/order?${out.toString()}`);
  };

  // Step 1 — email only. Saves the waitlist seat immediately (stage='email').
  async function submitWaitlist(e: React.FormEvent) {
    e.preventDefault();
    if (!wlEmail.trim() || wlState === 'busy') return;
    setWlState('busy');
    setWlError('');
    try {
      const res = await fetch(`${API}/api/programs/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: wlEmail.trim(),
          stage: 'email',
          source: regionCode ? `fg-landing-waitlist-${regionCode.toLowerCase()}` : 'fg-landing-waitlist',
          attribution: getAttributionPayload(),
          ...(cohortId ? { cohortId } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setWlError((d as any)?.error ?? 'Something went wrong — please try again.');
        setWlState('idle');
        return;
      }
      setWlState('idle');
      setWlStep('details');
    } catch {
      setWlError('Something went wrong — please try again.');
      setWlState('idle');
    }
  }

  // Step 2 — first name + last name + country, all required. Same
  // (program_slug, email) row from step 1 — this call marks it complete.
  async function submitWaitlistDetails(e: React.FormEvent) {
    e.preventDefault();
    if (!wlDetailsComplete || wlState === 'busy') return;
    setWlState('busy');
    setWlError('');
    try {
      const res = await fetch(`${API}/api/programs/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: wlEmail.trim(),
          firstName: wlFirstName.trim(),
          lastName: wlLastName.trim(),
          country: wlCountry,
          stage: 'complete',
          source: regionCode ? `fg-landing-waitlist-${regionCode.toLowerCase()}` : 'fg-landing-waitlist',
          attribution: getAttributionPayload(),
          ...(cohortId ? { cohortId } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setWlError((d as any)?.error ?? 'Something went wrong — please try again.');
        setWlState('idle');
        return;
      }
      setWlState('done');
    } catch {
      setWlError('Something went wrong — please try again.');
      setWlState('idle');
    }
  }

  const tierCtaLabel = (tier: FgTier) => (hasCommerce ? `Enrol — ${tier.name}` : 'Join the waitlist');
  const heroCtaLabel = hasCommerce ? 'Enrol in Freedom Gateway' : 'Join the next cohort waitlist';

  return (
    <div style={pageWrap}>
      <style>{`
        .fg-fit-yes::before { content: '✓'; margin-right: 10px; color: ${EMERALD}; font-weight: 800; }
        .fg-fit-no::before { content: '✕'; margin-right: 10px; color: #B84040; font-weight: 700; }
        .fg-check::before { content: '✓'; margin-right: 9px; color: ${EMERALD}; font-weight: 800; }
        details.fg-faq > summary { list-style: none; cursor: pointer; }
        details.fg-faq > summary::-webkit-details-marker { display: none; }
        details.fg-faq[open] > summary .fg-faq-plus { transform: rotate(45deg); }
      `}</style>

      {/* ── Top nav ── */}
      <header style={topNav}>
        <div style={topNavInner}>
          {data.brand?.logoUrl && (
            <a href="https://www.thesuccessfulbookkeeper.com" style={topNavLogoLink}>
              <img src={data.brand.logoUrl} alt={data.brand.name} style={topNavLogo} />
            </a>
          )}
          <div style={topNavRight}>
            <span style={topNavMeta}>
              <span style={{ color: EMERALD }}>●</span> {profile.regionLabel} · {kickoffLine}
            </span>
            <a href={`/lets-talk/${slug}`} style={topNavLink}>Talk with our team</a>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section style={heroSection}>
        <div style={heroInner}>
          <p style={heroEyebrow}>Freedom Gateway · 12-Month Program · Includes the Pure Bookkeeping System</p>
          <h1 style={heroH1}>Stop being the bottleneck in your own business.</h1>
          <p style={heroSub}>
            Freedom Gateway helps bookkeeping business owners install the systems, structure, and team
            habits that let your business run — without everything running through you.
          </p>
          <div style={heroCtaRow}>
            <button onClick={() => openTier(profile.tiers[0])} style={primaryCta}>{heroCtaLabel}</button>
            <a href={`/lets-talk/${slug}`} style={secondaryCta}>Want to talk it through first?</a>
          </div>
          <p style={heroFineprint}>
            {profile.tiers.map((t) => `${t.name} ${t.priceLabel}`).join('  ·  ')} · {kickoffLine}
          </p>
        </div>
      </section>

      {/* ── Pains ── */}
      <section style={sectionPale}>
        <div style={sectionInner}>
          <p style={sectionLabel}>Sound Familiar?</p>
          <h2 style={h2}>You're not bad at running a business. You just never had the systems to run it properly.</h2>
          <ul style={painList}>
            {PAINS.map((p, i) => (
              <li key={i} style={painItem}>{p}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Three 90-day shifts (deep emerald) ── */}
      <section style={sectionDeep}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabelLight}>What Freedom Gateway Actually Does</p>
            <h2 style={h2Inverse}>Three things change in 90 days</h2>
          </div>
          <div style={shiftGrid}>
            {SHIFTS.map((s, i) => (
              <div key={i} style={shiftCard}>
                <span style={shiftNum}>{i + 1}</span>
                <h3 style={shiftCardH3}>{s.title}</h3>
                <p style={shiftCardP}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Fit ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Is This Right For You?</p>
            <h2 style={h2}>For bookkeepers ready to stop doing everything themselves</h2>
          </div>
          <div style={fitGrid}>
            <div>
              <h3 style={fitColH3}>This is for you if…</h3>
              <ul style={fitList}>
                {FIT_YES.map((x, i) => <li key={i} style={fitItem} className="fg-fit-yes">{x}</li>)}
              </ul>
            </div>
            <div>
              <h3 style={fitColH3}>This is not for you if…</h3>
              <ul style={fitList}>
                {FIT_NO.map((x, i) => <li key={i} style={fitItem} className="fg-fit-no">{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── The 90-day transformation (region-aware) ── */}
      <section style={sectionCream}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>What's Inside</p>
            <h2 style={h2}>Your 90-day transformation</h2>
          </div>
          <div style={journeyList}>
            <div style={journeyStep}>
              <span style={journeyNum}>1</span>
              <div>
                <h3 style={journeyH3}>{kickoffHeading}</h3>
                <p style={journeyP}>{profile.kickoffNote}</p>
              </div>
            </div>
            <div style={journeyStep}>
              <span style={journeyNum}>2</span>
              <div>
                <h3 style={journeyH3}>Weekly coaching with {profile.coach.name} — {profile.cadence}</h3>
                <p style={journeyP}>
                  Every week, you'll work with {profile.coach.name} — a coach who built, scaled, and
                  ran a bookkeeping business. This isn't theory. It's guided implementation with
                  someone who's done it.
                </p>
              </div>
            </div>
            <div style={journeyStep}>
              <span style={journeyNum}>3</span>
              <div>
                <h3 style={journeyH3}>Freedom Framework course content</h3>
                <p style={journeyP}>
                  Work through the curriculum at your own pace between coaching sessions. Each module
                  builds on the last — from mapping your current operations to installing the system
                  that replaces you as the bottleneck.
                </p>
              </div>
            </div>
            <div style={journeyStep}>
              <span style={journeyNum}>4</span>
              <div>
                <h3 style={journeyH3}>Month 4 — Integration + Planning with {profile.lead.name}</h3>
                <p style={journeyP}>
                  After 90 days of implementation, month four consolidates what you've installed,
                  plans your next stage of growth, and sets the foundation for everything that comes
                  after.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Everything you get (program + PBS) ── */}
      <section style={section}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Everything You Get</p>
            <h2 style={h2}>Coaching + the system to implement</h2>
            <p style={{ ...lede, maxWidth: '640px', margin: '0 auto 44px' }}>
              Freedom Gateway doesn't just teach you what to do — it gives you the actual system to
              install. The Pure Bookkeeping System is included from day one.
            </p>
          </div>
          <div style={getGrid}>
            <div style={getCard}>
              <span style={getTag}>Freedom Gateway Program</span>
              <h3 style={getH3}>90-Day Guided Implementation</h3>
              <ul style={getList}>
                {PROGRAM_INCLUDES.map((x, i) => <li key={i} style={getItem} className="fg-check">{x}</li>)}
              </ul>
            </div>
            <div style={{ ...getCard, ...getCardGold }}>
              <span style={{ ...getTag, color: '#8B6E00', background: 'rgba(245,195,0,0.16)' }}>
                Pure Bookkeeping System
              </span>
              <h3 style={getH3}>Basic Edition License — Included</h3>
              <p style={{ ...getItem, listStyle: 'none', margin: '0 0 14px' }}>
                The core operating system built by 43,000+ bookkeepers. Pre-built workflows,
                checklists, and processes for every stage of client work.
              </p>
              <ul style={getList}>
                {PBS_INCLUDES.map((x, i) => <li key={i} style={getItem} className="fg-check">{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing — two tiers ── */}
      <section style={sectionDeep} id="pricing">
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabelLight}>Your Investment</p>
            <h2 style={h2Inverse}>Coaching + the system. One price.</h2>
            <p style={{ ...ledeInverse, maxWidth: '560px', margin: '0 auto 44px' }}>
              {hasCommerce
                ? 'Two ways in. Both include the Pure Bookkeeping System and your full year of coaching.'
                : 'Enrollment for the next cohort opens soon — join the waitlist below and you’ll be first to know.'}
            </p>
          </div>
          <div style={tierGrid}>
            {profile.tiers.map((tier) => (
              <div key={tier.id} style={{ ...tierCard, ...(tier.highlight ? tierCardHighlight : {}) }}>
                {tier.badge && (
                  <span style={{ ...tierBadge, ...(tier.highlight ? tierBadgeGold : {}) }}>{tier.badge}</span>
                )}
                <p style={tierName}>{tier.name}</p>
                {tier.strikeLabel && <p style={tierStrike}>{tier.strikeLabel}</p>}
                <p style={tierPrice}>{tier.priceLabel}</p>
                <p style={tierTerms}>{tier.terms}</p>
                {tier.saveLabel && <p style={tierSave}>{tier.saveLabel}</p>}
                <ul style={tierBullets}>
                  {tier.bullets.map((b, i) => <li key={i} style={tierBullet} className="fg-check">{b}</li>)}
                </ul>
                <p style={tierPbs}>{tier.pbsLine}</p>
                <button onClick={() => openTier(tier)} style={tier.highlight ? tierCtaGold : tierCta}>
                  {tierCtaLabel(tier)}
                </button>
                <p style={tierKicker}>{kickoffLine}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={sectionPale}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Real Transformations</p>
            <h2 style={h2}>They were where you are</h2>
          </div>
          <div style={quoteGrid}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={quoteCard}>
                <p style={quoteText}>"{t.quote}"</p>
                <p style={quoteName}>{t.name}</p>
                <p style={quoteTag}>{t.tag}</p>
              </div>
            ))}
          </div>
          <div style={proofStrip}>
            {PROOF_STATS.map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={proofNum}>{s.num}</div>
                <div style={proofLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Coaches ── */}
      <section style={section}>
        <div style={sectionWide}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Your Coaches</p>
            <h2 style={h2}>Who you'll work with</h2>
          </div>
          <div style={coachGrid}>
            {[profile.coach, profile.lead].map((person, i) => (
              <div key={i} style={coachCard}>
                <div style={coachPhotoWrap}>
                  <img src={person.photo} alt={person.name} style={coachPhoto} />
                </div>
                <p style={coachName}>{person.name}</p>
                <p style={coachRole}>{person.title}</p>
                <p style={coachBio}>{person.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={sectionCream}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Common Questions</p>
            <h2 style={h2}>Everything you need to know</h2>
          </div>
          <div style={{ marginTop: '36px' }}>
            {FAQS.map((f, i) => (
              <details key={i} className="fg-faq" style={faqItem}>
                <summary style={faqSummary}>
                  <span>{f.q}</span>
                  <span className="fg-faq-plus" style={faqPlus}>+</span>
                </summary>
                <p style={faqAnswer}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Waitlist (no-commerce mode) ── */}
      {!hasCommerce && (
        <section style={section} id="waitlist">
          <div style={{ ...sectionInner, maxWidth: '560px', textAlign: 'center' }}>
            <p style={sectionLabel}>Next Cohort</p>
            <h2 style={h2}>Join the waitlist for the next Freedom Gateway intake</h2>
            {wlState === 'done' ? (
              <p style={{ ...lede, color: EMERALD, fontWeight: 600 }}>
                You're on the list. We'll email you the moment the next cohort opens.
              </p>
            ) : wlStep === 'email' ? (
              <form onSubmit={submitWaitlist} style={wlForm}>
                <input
                  type="email"
                  required
                  placeholder="you@yourfirm.com"
                  value={wlEmail}
                  onChange={(e) => setWlEmail(e.target.value)}
                  style={wlInput}
                />
                <button type="submit" disabled={wlState === 'busy'} style={primaryCta}>
                  {wlState === 'busy' ? 'One sec…' : 'Notify me'}
                </button>
              </form>
            ) : (
              <form onSubmit={submitWaitlistDetails} style={{ ...wlForm, flexDirection: 'column', alignItems: 'stretch', maxWidth: '360px', margin: '24px auto 0' }}>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="First name"
                  value={wlFirstName}
                  onChange={(e) => setWlFirstName(e.target.value)}
                  style={wlInput}
                />
                <input
                  type="text"
                  required
                  placeholder="Last name"
                  value={wlLastName}
                  onChange={(e) => setWlLastName(e.target.value)}
                  style={wlInput}
                />
                <CountrySelect value={wlCountry} onChange={setWlCountry} style={wlInput} />
                <button type="submit" disabled={wlState === 'busy' || !wlDetailsComplete} style={primaryCta}>
                  {wlState === 'busy' ? 'One sec…' : 'Done →'}
                </button>
              </form>
            )}
            {wlError && <p style={modalError}>{wlError}</p>}
            <p style={{ ...heroFineprint, color: 'rgba(10,10,46,0.55)', marginTop: '14px' }}>
              No spam — just the announcement and details.
            </p>
          </div>
        </section>
      )}

      {/* ── Final CTA ── */}
      <section style={sectionDeep}>
        <div style={{ ...sectionInner, textAlign: 'center' }}>
          <h2 style={{ ...h2Inverse, maxWidth: '760px', margin: '0 auto 18px' }}>
            You didn't start your business to be trapped inside it.
          </h2>
          <p style={{ ...ledeInverse, maxWidth: '620px', margin: '0 auto 32px' }}>
            Freedom Gateway exists because Lisa built this exact transformation for herself — then
            spent a decade helping other bookkeepers do the same. Real systems. Real coaching. A
            business that actually gives you freedom.
          </p>
          <div style={{ ...heroCtaRow, justifyContent: 'center' }}>
            <button onClick={() => openTier(profile.tiers[0])} style={primaryCta}>{heroCtaLabel}</button>
            <a href={`/lets-talk/${slug}`} style={secondaryCtaLight}>Talk with our team first</a>
          </div>
        </div>
      </section>

      <footer style={footer}>
        <p style={footerMeta}>{kickoffLine} · Includes the Pure Bookkeeping System · Payments processed in USD</p>
        <PolicyLinks />
      </footer>

      <BuyerIntentModal
        open={modalOpen}
        tierName={modalTier.name}
        priceLabel={modalTier.priceLabel}
        onClose={() => setModalOpen(false)}
        onSubmit={handleIntent}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline styles — emerald/gold (consumes --prog-* with fallbacks)
// ────────────────────────────────────────────────────────────────────────────

const EMERALD = 'var(--prog-primary, #00C896)';
const GOLD = 'var(--prog-highlight, #F5C300)';
const NAVY = 'var(--prog-text-dark, #0A0A2E)';
const DEEP = '#013027'; // deep emerald for dark panels (not the navy --prog-deep)
const TINT = 'var(--prog-tint, #E6FAF4)';
const CANVAS = 'var(--prog-canvas, #F8F6F3)';
const FONT_HEADING = 'var(--font-heading, "Poppins", -apple-system, sans-serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = { minHeight: '100vh', background: CANVAS, color: NAVY, fontFamily: FONT_BODY };

const topNav: React.CSSProperties = { background: '#FFFFFF', borderBottom: '1px solid rgba(10,10,46,0.08)', padding: '12px 24px' };
const topNavInner: React.CSSProperties = { maxWidth: '1140px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' };
const topNavLogoLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
const topNavLogo: React.CSSProperties = { height: '34px', width: 'auto', display: 'block' };
const topNavRight: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' };
const topNavMeta: React.CSSProperties = { fontSize: '12.5px', fontWeight: 600, color: 'rgba(10,10,46,0.66)' };
const topNavLink: React.CSSProperties = { fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, color: EMERALD, textDecoration: 'none', borderBottom: `2px solid ${EMERALD}`, paddingBottom: '1px' };

const heroSection: React.CSSProperties = { background: `linear-gradient(160deg, ${DEEP} 0%, #02160F 100%)`, color: '#fff', padding: '88px 24px 96px' };
const heroInner: React.CSSProperties = { maxWidth: '860px', margin: '0 auto', textAlign: 'center' };
const heroEyebrow: React.CSSProperties = { fontSize: '13px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: GOLD, margin: '0 0 20px' };
const heroH1: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: 'clamp(34px, 5vw, 56px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 22px' };
const heroSub: React.CSSProperties = { fontSize: '18px', lineHeight: 1.6, color: 'rgba(255,255,255,0.82)', maxWidth: '660px', margin: '0 auto 32px' };
const heroCtaRow: React.CSSProperties = { display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '20px' };
const primaryCta: React.CSSProperties = { fontFamily: FONT_BODY, fontSize: '16px', fontWeight: 700, color: '#02160F', background: EMERALD, border: 'none', borderRadius: '10px', padding: '15px 30px', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,200,150,0.3)' };
const secondaryCta: React.CSSProperties = { fontFamily: FONT_BODY, fontSize: '15px', fontWeight: 600, color: '#fff', textDecoration: 'none', border: '1.5px solid rgba(255,255,255,0.5)', borderRadius: '10px', padding: '14px 26px', display: 'inline-flex', alignItems: 'center' };
const secondaryCtaLight = secondaryCta;
const heroFineprint: React.CSSProperties = { fontSize: '13.5px', color: 'rgba(255,255,255,0.6)', margin: 0 };

const section: React.CSSProperties = { padding: '76px 24px', background: CANVAS };
const sectionPale: React.CSSProperties = { padding: '76px 24px', background: '#fff' };
const sectionCream: React.CSSProperties = { padding: '76px 24px', background: TINT };
const sectionDeep: React.CSSProperties = { padding: '80px 24px', background: `linear-gradient(165deg, ${DEEP} 0%, #02160F 100%)`, color: '#fff' };
const sectionInner: React.CSSProperties = { maxWidth: '820px', margin: '0 auto' };
const sectionWide: React.CSSProperties = { maxWidth: '1100px', margin: '0 auto' };

const sectionLabel: React.CSSProperties = { fontSize: '12.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: EMERALD, margin: '0 0 12px' };
const sectionLabelLight: React.CSSProperties = { ...sectionLabel, color: GOLD };
const h2: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em', margin: '0 0 20px', color: NAVY };
const h2Inverse: React.CSSProperties = { ...h2, color: '#fff' };
const lede: React.CSSProperties = { fontSize: '17px', lineHeight: 1.6, color: 'rgba(10,10,46,0.72)' };
const ledeInverse: React.CSSProperties = { fontSize: '17px', lineHeight: 1.6, color: 'rgba(255,255,255,0.8)' };

const painList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '28px 0 0', display: 'grid', gap: '14px' };
const painItem: React.CSSProperties = { fontSize: '16.5px', lineHeight: 1.5, padding: '16px 20px', background: CANVAS, borderRadius: '10px', borderLeft: `3px solid ${EMERALD}` };

const shiftGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '22px', marginTop: '44px' };
const shiftCard: React.CSSProperties = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '28px 24px' };
const shiftNum: React.CSSProperties = { display: 'inline-flex', width: '34px', height: '34px', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: EMERALD, color: '#02160F', fontWeight: 800, fontSize: '16px', marginBottom: '14px' };
const shiftCardH3: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '20px', fontWeight: 700, margin: '0 0 10px', color: '#fff' };
const shiftCardP: React.CSSProperties = { fontSize: '15px', lineHeight: 1.55, color: 'rgba(255,255,255,0.76)', margin: 0 };

const fitGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', marginTop: '36px' };
const fitColH3: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '18px', fontWeight: 700, margin: '0 0 16px' };
const fitList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '12px' };
const fitItem: React.CSSProperties = { fontSize: '15.5px', lineHeight: 1.5, display: 'flex' };

const journeyList: React.CSSProperties = { display: 'grid', gap: '18px', marginTop: '40px', maxWidth: '760px', marginInline: 'auto' };
const journeyStep: React.CSSProperties = { display: 'flex', gap: '18px', alignItems: 'flex-start', background: '#fff', borderRadius: '14px', padding: '24px 26px', boxShadow: '0 2px 16px rgba(10,10,46,0.05)' };
const journeyNum: React.CSSProperties = { flexShrink: 0, width: '38px', height: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: TINT, color: EMERALD, fontWeight: 800, fontSize: '17px' };
const journeyH3: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '18px', fontWeight: 700, margin: '0 0 6px' };
const journeyP: React.CSSProperties = { fontSize: '15px', lineHeight: 1.55, color: 'rgba(10,10,46,0.7)', margin: 0 };

const getGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', marginTop: '40px' };
const getCard: React.CSSProperties = { background: '#fff', border: '1px solid rgba(10,10,46,0.08)', borderRadius: '16px', padding: '32px 30px' };
const getCardGold: React.CSSProperties = { border: '1px solid rgba(245,195,0,0.4)', background: 'linear-gradient(180deg, #fffdf5 0%, #fff 100%)' };
const getTag: React.CSSProperties = { display: 'inline-block', fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: EMERALD, background: TINT, borderRadius: '999px', padding: '5px 12px', marginBottom: '14px' };
const getH3: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '21px', fontWeight: 700, margin: '0 0 16px' };
const getList: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '11px' };
const getItem: React.CSSProperties = { fontSize: '15px', lineHeight: 1.5, display: 'flex', color: 'rgba(10,10,46,0.82)' };

const tierGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', marginTop: '12px', alignItems: 'stretch' };
const tierCard: React.CSSProperties = { background: '#fff', color: NAVY, borderRadius: '18px', padding: '34px 30px', display: 'flex', flexDirection: 'column', position: 'relative', border: '1px solid rgba(255,255,255,0.16)' };
const tierCardHighlight: React.CSSProperties = { boxShadow: `0 0 0 2px ${GOLD}, 0 24px 60px rgba(0,0,0,0.35)` };
const tierBadge: React.CSSProperties = { position: 'absolute', top: '-13px', left: '30px', fontSize: '12px', fontWeight: 700, color: '#02160F', background: EMERALD, borderRadius: '999px', padding: '5px 14px' };
const tierBadgeGold: React.CSSProperties = { background: GOLD, color: '#5b4a00' };
const tierName: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '24px', fontWeight: 800, margin: '6px 0 4px' };
const tierStrike: React.CSSProperties = { fontSize: '15px', color: 'rgba(10,10,46,0.4)', textDecoration: 'line-through', margin: '0 0 2px' };
const tierPrice: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '30px', fontWeight: 800, color: EMERALD, margin: '0 0 8px' };
const tierTerms: React.CSSProperties = { fontSize: '13.5px', color: 'rgba(10,10,46,0.6)', margin: '0 0 10px' };
const tierSave: React.CSSProperties = { fontSize: '13.5px', fontWeight: 700, color: '#8B6E00', background: 'rgba(245,195,0,0.15)', borderRadius: '8px', padding: '8px 12px', margin: '0 0 16px' };
const tierBullets: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '4px 0 16px', display: 'grid', gap: '10px' };
const tierBullet: React.CSSProperties = { fontSize: '14.5px', lineHeight: 1.45, display: 'flex', color: 'rgba(10,10,46,0.85)' };
const tierPbs: React.CSSProperties = { fontSize: '13px', lineHeight: 1.45, color: 'rgba(10,10,46,0.6)', borderTop: '1px solid rgba(10,10,46,0.08)', paddingTop: '14px', margin: '0 0 18px' };
const tierCta: React.CSSProperties = { ...primaryCta, width: '100%', marginTop: 'auto' };
const tierCtaGold: React.CSSProperties = { ...primaryCta, width: '100%', marginTop: 'auto', background: GOLD, color: '#5b4a00', boxShadow: '0 10px 30px rgba(245,195,0,0.32)' };
const tierKicker: React.CSSProperties = { fontSize: '12.5px', color: 'rgba(10,10,46,0.5)', textAlign: 'center', margin: '12px 0 0' };

const quoteGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '40px' };
const quoteCard: React.CSSProperties = { background: CANVAS, borderRadius: '14px', padding: '26px 24px' };
const quoteText: React.CSSProperties = { fontSize: '15.5px', lineHeight: 1.55, color: 'rgba(10,10,46,0.85)', margin: '0 0 16px' };
const quoteName: React.CSSProperties = { fontWeight: 700, fontSize: '15px', margin: '0 0 2px' };
const quoteTag: React.CSSProperties = { fontSize: '13px', color: EMERALD, fontWeight: 600, margin: 0 };
const proofStrip: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '20px', marginTop: '44px', padding: '32px 0 0', borderTop: '1px solid rgba(10,10,46,0.08)' };
const proofNum: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '34px', fontWeight: 800, color: EMERALD };
const proofLabel: React.CSSProperties = { fontSize: '13.5px', color: 'rgba(10,10,46,0.6)', lineHeight: 1.4, marginTop: '4px' };

const coachGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '28px', marginTop: '40px', maxWidth: '760px', marginInline: 'auto' };
const coachCard: React.CSSProperties = { textAlign: 'center', background: '#fff', borderRadius: '16px', padding: '32px 26px', boxShadow: '0 2px 16px rgba(10,10,46,0.05)' };
const coachPhotoWrap: React.CSSProperties = { width: '108px', height: '108px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 18px', border: `3px solid ${TINT}` };
const coachPhoto: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };
const coachName: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '20px', fontWeight: 700, margin: '0 0 4px' };
const coachRole: React.CSSProperties = { fontSize: '13.5px', fontWeight: 600, color: EMERALD, margin: '0 0 12px' };
const coachBio: React.CSSProperties = { fontSize: '14.5px', lineHeight: 1.55, color: 'rgba(10,10,46,0.72)', margin: 0 };

const faqItem: React.CSSProperties = { borderBottom: '1px solid rgba(10,10,46,0.1)', padding: '4px 0' };
const faqSummary: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '20px 4px', fontFamily: FONT_HEADING, fontSize: '17px', fontWeight: 600, color: NAVY };
const faqPlus: React.CSSProperties = { flexShrink: 0, fontSize: '22px', color: EMERALD, transition: 'transform 0.2s', lineHeight: 1 };
const faqAnswer: React.CSSProperties = { fontSize: '15px', lineHeight: 1.6, color: 'rgba(10,10,46,0.72)', padding: '0 4px 20px', margin: 0 };

const wlForm: React.CSSProperties = { display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '24px' };
const wlInput: React.CSSProperties = { flex: '1 1 240px', fontFamily: FONT_BODY, fontSize: '16px', padding: '14px 16px', borderRadius: '10px', border: '1.5px solid rgba(10,10,46,0.2)', minWidth: 0 };

const footer: React.CSSProperties = { background: '#fff', borderTop: '1px solid rgba(10,10,46,0.08)', padding: '32px 24px', textAlign: 'center' };
const footerMeta: React.CSSProperties = { fontSize: '13px', color: 'rgba(10,10,46,0.55)', margin: '0 0 14px' };

// Modal
const modalOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(2,22,15,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 };
const modalCard: React.CSSProperties = { background: '#fff', borderRadius: '18px', padding: '34px 32px', maxWidth: '440px', width: '100%', position: 'relative', fontFamily: FONT_BODY, color: NAVY };
const modalCloseBtn: React.CSSProperties = { position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none', fontSize: '26px', lineHeight: 1, cursor: 'pointer', color: 'rgba(10,10,46,0.4)' };
const modalEyebrow: React.CSSProperties = { fontSize: '12.5px', fontWeight: 700, color: EMERALD, margin: '0 0 8px' };
const modalH2: React.CSSProperties = { fontFamily: FONT_HEADING, fontSize: '22px', fontWeight: 800, margin: '0 0 8px' };
const modalSub: React.CSSProperties = { fontSize: '14.5px', lineHeight: 1.5, color: 'rgba(10,10,46,0.66)', margin: '0 0 20px' };
const modalForm: React.CSSProperties = { display: 'grid', gap: '14px' };
const modalRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };
const modalLabel: React.CSSProperties = { display: 'grid', gap: '6px', fontSize: '13px', fontWeight: 600, color: 'rgba(10,10,46,0.7)' };
const modalInput: React.CSSProperties = { fontFamily: FONT_BODY, fontSize: '15px', padding: '12px 14px', borderRadius: '9px', border: '1.5px solid rgba(10,10,46,0.18)' };
const modalError: React.CSSProperties = { fontSize: '13.5px', color: '#B84040', margin: '4px 0 0' };
const modalSubmit: React.CSSProperties = { ...primaryCta, width: '100%', marginTop: '4px' };
const modalFineprint: React.CSSProperties = { fontSize: '12px', color: 'rgba(10,10,46,0.5)', textAlign: 'center', margin: '4px 0 0' };
